import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { useWebSocket } from "@/context/WebSocketContext";
import { WS_CHANNELS } from "@/utils/websocket";
import { CallToAction } from "./CallToAction";
import type { PodTemplate } from "@/services/api/agentic/types";

interface CallToActionItem {
  id: string;
  pod_instance_id: string;
  task_id: string;
  category: string;
  remedy_type: string;
  message: string;
  priority: string;
  status: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

interface PodInstanceGroup {
  pod_instance_id: string;
  pod_instance_name: string;
  ctas: CallToActionItem[];
}

interface ActionsProps {
  templateId: string;
  fetchCallToActions: (templateId: string) => Promise<CallToActionItem[]>;
  fetchPodInstances: (
    templateId: string
  ) => Promise<Array<{ id: string; name: string }>>;
  podTemplate?: PodTemplate;
}

export function Actions({
  templateId,
  fetchCallToActions,
  fetchPodInstances,
  podTemplate,
}: ActionsProps) {
  const [groupedCtas, setGroupedCtas] = useState<PodInstanceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();

  // Fetch and group CTAs by pod instance
  const loadActions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [ctas, podInstances] = await Promise.all([
        fetchCallToActions(templateId),
        fetchPodInstances(templateId),
      ]);

      // Create a map of pod instance IDs to names
      const podInstanceMap = new Map(
        podInstances.map((instance) => [instance.id, instance.name])
      );

      // Group CTAs by pod instance
      const groups = ctas.reduce((acc, cta) => {
        const podInstanceId = cta.pod_instance_id;
        const podInstanceName =
          podInstanceMap.get(podInstanceId) || `Instance ${podInstanceId}`;

        let group = acc.find((g) => g.pod_instance_id === podInstanceId);
        if (!group) {
          group = {
            pod_instance_id: podInstanceId,
            pod_instance_name: podInstanceName,
            ctas: [],
          };
          acc.push(group);
        }

        group.ctas.push(cta);
        return acc;
      }, [] as PodInstanceGroup[]);

      // Sort groups by name and CTAs by priority (high > medium > low) then created_at
      groups.sort((a, b) =>
        a.pod_instance_name.localeCompare(b.pod_instance_name)
      );
      groups.forEach((group) => {
        group.ctas.sort((a, b) => {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          const aPriority =
            priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
          const bPriority =
            priorityOrder[b.priority as keyof typeof priorityOrder] || 0;

          if (aPriority !== bPriority) {
            return bPriority - aPriority; // Higher priority first
          }

          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        });
      });

      setGroupedCtas(groups);
    } catch (err) {
      console.error("Failed to fetch actions:", err);
      setError(err instanceof Error ? err.message : "Failed to load actions");
    } finally {
      setLoading(false);
    }
  }, [templateId, fetchCallToActions, fetchPodInstances]);

  // Initial load
  useEffect(() => {
    loadActions();
  }, [loadActions]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    const channel = WS_CHANNELS.CALL_TO_ACTIONS;
    subscribe(channel);

    return () => {
      unsubscribe(channel);
    };
  }, [subscribe, unsubscribe]);

  // Handle WebSocket messages for real-time updates
  useEffect(() => {
    const message: any = lastMessage.get(WS_CHANNELS.CALL_TO_ACTIONS);

    if (message && message.full_document) {
      // Always reload on any CTA change for this template
      // The loadActions function will filter to the correct template
      loadActions();
    }
  }, [lastMessage, loadActions]);

  if (loading) {
    return <div className="p-4 flex h-full w-full items-center justify-center text-slate-600 dark:text-slate-400">Loading actions...</div>;
  }

  if (error) {
    return (
      <div className="p-4 flex h-full w-full text-red-500">Error: {error}</div>
    );
  }

  if (groupedCtas.length === 0) {
    return (
      <div className="p-4 flex flex-col items-center justify-center h-full w-full text-slate-500 dark:text-slate-400">
        <div className="text-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-4xl mb-4 opacity-50"
          />
          <h3 className="text-lg font-medium mb-2 text-slate-700 dark:text-slate-300">No Active Actions</h3>
          <p className="text-sm">
            There are currently no call-to-actions requiring your attention for
            this pod.
          </p>
        </div>
      </div>
    );
  }

  const totalCtas = groupedCtas.reduce(
    (sum, group) => sum + group.ctas.length,
    0
  );

  return (
    <div className="p-4 flex flex-col h-full w-full flex-grow">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Actions Required</h2>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {totalCtas} action{totalCtas !== 1 ? "s" : ""} across{" "}
            {groupedCtas.length} instance{groupedCtas.length !== 1 ? "s" : ""}
          </div>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Complete these actions to keep your workflows moving forward.
        </p>
      </div>

      <div className="space-y-6 overflow-y-auto flex-grow">
        {groupedCtas.map((group) => (
          <div
            key={group.pod_instance_id}
            className="bg-white dark:bg-gray-800 rounded-2xl"
          >
            <div className=" dark:border-gray-700 px-4 py-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {group.pod_instance_name}
                </h3>
                <span className=" text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-900/10 dark:bg-slate-100/10 px-2 py-0.5 rounded-full">
                  <div className="top-[1px] relative">{group.ctas.length}</div>
                </span>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {group.ctas.map((cta, index) => (
                <div
                  key={cta.id || (cta as any)._id || `cta-${index}`}
                  className="[&_input]:bg-gray-50 [&_input]:dark:bg-gray-900/50 [&_input]:border [&_input]:border-gray-200 [&_input]:dark:border-gray-600"
                >
                  <CallToAction action={cta} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
